<template>
    <div class="bg-white p-6 shadow-md rounded-lg">
        <h2 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <font-awesome-icon :icon="['fas', 'building']" class="text-blue-600 mr-2" />
            Room Utilization
        </h2>

        <!-- Summary Cards -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div class="bg-purple-50 p-3 rounded-lg">
                <p class="text-xs text-purple-600 font-medium">Total Rooms</p>
                <p class="text-lg font-bold text-purple-800">{{ getTotalRooms() }}</p>
            </div>
            <div class="bg-blue-50 p-3 rounded-lg">
                <p class="text-xs text-blue-600 font-medium">Total Classes</p>
                <p class="text-lg font-bold text-blue-800">{{ getTotalClasses() }}</p>
            </div>
            <div class="bg-green-50 p-3 rounded-lg">
                <p class="text-xs text-green-600 font-medium">Capacity Utilization</p>
                <p class="text-lg font-bold text-green-800">{{ getCapacityUtilization() }}%</p>
            </div>
            <div class="bg-yellow-50 p-3 rounded-lg">
                <p class="text-xs text-yellow-600 font-medium">Enrollment Utilization</p>
                <p class="text-lg font-bold text-yellow-800">{{ getEnrollmentUtilization() }}%</p>
            </div>
        </div>

        <div v-if="hasData">
            <!-- Charts Container -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <!-- Capacity vs Enrollment Chart -->
                <div>
                    <h4 class="text-md font-medium text-gray-700 mb-3">Capacity vs Enrollment Utilization</h4>
                    <div class="h-80">
                        <apexchart type="bar" height="100%" :options="utilizationOptions" :series="utilizationSeries" />
                    </div>
                </div>

                <!-- Room Capacity Distribution -->
                <div>
                    <h4 class="text-md font-medium text-gray-700 mb-3">Classes by Room Capacity</h4>

                    <!-- Capacity Legend -->
                    <div class="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                        <h5 class="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <font-awesome-icon :icon="['fas', 'info-circle']" class="text-blue-600 mr-2" />
                            Room Capacity Categories
                        </h5>
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div class="flex items-center space-x-3">
                                <div class="w-4 h-4 rounded-full bg-blue-500"></div>
                                <div>
                                    <span class="text-sm font-medium text-gray-700">Small</span>
                                    <p class="text-xs text-gray-500">≤ 25 seats</p>
                                </div>
                            </div>
                            <div class="flex items-center space-x-3">
                                <div class="w-4 h-4 rounded-full bg-yellow-500"></div>
                                <div>
                                    <span class="text-sm font-medium text-gray-700">Medium</span>
                                    <p class="text-xs text-gray-500">26 - 50 seats</p>
                                </div>
                            </div>
                            <div class="flex items-center space-x-3">
                                <div class="w-4 h-4 rounded-full bg-red-500"></div>
                                <div>
                                    <span class="text-sm font-medium text-gray-700">Large</span>
                                    <p class="text-xs text-gray-500">> 50 seats</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="h-80">
                        <apexchart type="donut" height="100%" :options="capacityRangeOptions"
                            :series="capacityRangeSeries" />
                    </div>
                </div>
            </div>

            <!-- Top Utilized Rooms Chart -->
            <div class="mt-6">
                <h4 class="text-md font-medium text-gray-700 mb-3">Top Utilized Rooms</h4>
                <div class="h-96">
                    <apexchart type="bar" height="100%" :options="topRoomsOptions" :series="topRoomsSeries"
                        @dataPointSelection="handleBarClick" />
                </div>

                <!-- Others Rooms Modal -->
                <base-modal v-if="showOthersModal" title="Other Utilized Rooms" height=""
                    @close="showOthersModal = false">
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-100 sticky top-0 z-10">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        #
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Room
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Class Count
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Room Capacity
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Max Enrollment
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Enrolled Students
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-100">
                                <tr v-for="(room, index) in otherRoomsData" :key="index"
                                    class="hover:bg-gray-50 transition-colors">
                                    <td class="px-6 py-4 text-sm text-gray-500 text-center">{{ index + 1 }}</td>
                                    <td class="px-6 py-4 text-sm text-gray-900 font-medium">{{ room.room }}</td>
                                    <td class="px-6 py-4 text-sm text-gray-800 text-center font-medium">
                                        {{ room.class_count }}
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-800 text-center">
                                        {{ room.room_capacity }}
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-800 text-center">
                                        {{ room.total_max_enrollment }}
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-800 text-center">
                                        {{ room.total_enrolled_students }}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="bg-gray-50 font-semibold">
                                <tr>
                                    <td class="px-6 py-3 text-center text-gray-700" colspan="2">Total</td>
                                    <td class="px-6 py-3 text-center text-gray-900">{{ totalClassCount }}</td>
                                    <td class="px-6 py-3 text-center text-gray-900">{{ totalRoomCapacity }}</td>
                                    <td class="px-6 py-3 text-center text-gray-900">{{ totalMaxEnrollment }}</td>
                                    <td class="px-6 py-3 text-center text-gray-900">{{ totalEnrolledStudents }}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </base-modal>
            </div>
        </div>
        <div v-else class="text-gray-500 text-center py-16">
            <div class="text-4xl mb-2">🏢</div>
            <div>There is no room utilization data to show</div>
        </div>
    </div>
</template>

<script>
export default {
    name: "RoomUtilizationChart",
    props: {
        data: {
            type: Object,
            required: true
        },
        powerBiColors: {
            type: Array,
            required: true
        },
        fetchOtherRooms: {
            type: Function,
            required: true
        }
    },
    data() {
        return {
            showOthersModal: false,
            otherRoomsData: []
        };
    },
    computed: {
        hasData() {
            return this.data && this.data.summary && this.data.summary.total_classes > 0;
        },
        utilizationSeries() {
            return [
                {
                    name: 'Capacity Utilization',
                    data: [this.getCapacityUtilization()]
                },
                {
                    name: 'Enrollment Utilization',
                    data: [this.getEnrollmentUtilization()]
                }
            ];
        },
        utilizationOptions() {
            return {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '60%',
                        endingShape: 'rounded',
                        distributed: false
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + '%';
                    }
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                xaxis: {
                    categories: ['Utilization'],
                    title: {
                        text: 'Utilization Type'
                    }
                },
                yaxis: {
                    title: {
                        text: 'Percentage (%)'
                    },
                    max: 100
                },
                fill: {
                    opacity: 1,
                    colors: [this.powerBiColors[0], this.powerBiColors[1]]
                },
                colors: [this.powerBiColors[0], this.powerBiColors[1]],
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val.toFixed(1) + '%';
                        }
                    }
                },
                legend: {
                    position: 'top'
                }
            };
        },
        capacityRangeSeries() {
            const ranges = this.getCapacityRanges();
            return Object.values(ranges);
        },
        capacityRangeOptions() {
            const ranges = this.getCapacityRanges();
            const labels = Object.keys(ranges).map(key => {
                // Capitalize first letter for display
                return key.charAt(0).toUpperCase() + key.slice(1);
            });

            // Define colors that match the legend
            const capacityColors = {
                'Small': '#3B82F6',   // Blue
                'Medium': '#EAB308',  // Yellow
                'Large': '#EF4444'    // Red
            };

            const colors = labels.map(label => capacityColors[label] || this.powerBiColors[0]);

            return {
                labels: labels,
                legend: {
                    position: 'bottom',
                    fontSize: '12px',
                    fontWeight: 500,
                    formatter: function (val, opts) {
                        return val + " - " + opts.w.globals.series[opts.seriesIndex]
                    }
                },
                colors: colors,
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + '%';
                    }
                },
                tooltip: {
                    enabled: true,
                    custom: function ({ series, seriesIndex, w }) {
                        const label = w.config.labels[seriesIndex];
                        const value = series[seriesIndex];
                        const capacityInfo = {
                            'Small': '≤ 25 seats',
                            'Medium': '26 - 50 seats',
                            'Large': '> 50 seats'
                        };

                        return `
                            <div class="bg-white p-3 rounded-lg shadow-lg border">
                                <div class="flex items-center space-x-2">
                                    <div class="w-3 h-3 rounded-full" style="background-color: ${w.config.colors[seriesIndex]}"></div>
                                    <span class="font-semibold text-gray-800">${label}</span>
                                </div>
                                <div class="mt-1">
                                    <div class="text-sm text-gray-600">${value} classes</div>
                                    <div class="text-xs text-gray-500">${capacityInfo[label] || label}</div>
                                </div>
                            </div>
                        `;
                    }
                }
            };
        },
        topRoomsSeries() {
            const topRooms = this.getTopUtilizedRooms();
            const othersCount = this.getOthersCount();

            // Combine top 10 rooms with others
            const allRooms = [...topRooms];
            if (othersCount > 0) {
                allRooms.push({ room: 'Others', class_count: othersCount });
            }

            return [{
                name: 'Classes',
                data: allRooms.map(room => room.class_count)
            }];
        },
        topRoomsOptions() {
            const topRooms = this.getTopUtilizedRooms();
            const othersCount = this.getOthersCount();

            // Combine top 10 rooms with others
            const allRooms = [...topRooms];
            if (othersCount > 0) {
                allRooms.push({ room: 'Others', class_count: othersCount });
            }

            const colors = allRooms.map((room, index) => {
                if (room.room === 'Others') {
                    return '#6B7280'; // Gray for Others
                }
                return this.powerBiColors[index % this.powerBiColors.length];
            });

            return {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '60%',
                        distributed: true,
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val;
                    },
                    offsetX: 0,
                    style: {
                        fontSize: '12px',
                        colors: ['#fff']
                    }
                },
                xaxis: {
                    categories: allRooms.map(room => room.room),
                    title: {
                        text: 'Number of Classes'
                    }
                },
                yaxis: {
                    title: {
                        text: 'Rooms'
                    }
                },
                colors: colors,
                tooltip: {
                    y: {
                        formatter: function (val, { dataPointIndex, w }) {
                            const roomName = w.config.xaxis.categories[dataPointIndex];
                            return `${val} classes in ${roomName}`;
                        }
                    }
                },
                legend: {
                    show: false
                }
            };
        },
        totalClassCount() {
            return this.otherRoomsData.reduce((sum, room) => sum + (room.class_count || 0), 0);
        },
        totalRoomCapacity() {
            return this.otherRoomsData.reduce((sum, room) => sum + (room.room_capacity || 0), 0);
        },
        totalMaxEnrollment() {
            return this.otherRoomsData.reduce((sum, room) => sum + (room.total_max_enrollment || 0), 0);
        },
        totalEnrolledStudents() {
            return this.otherRoomsData.reduce((sum, room) => sum + (room.total_enrolled_students || 0), 0);
        }
    },
    methods: {
        getTotalRooms() {
            if (this.data && this.data.summary && this.data.summary.total_rooms !== undefined) {
                return this.data.summary.total_rooms;
            }
            return 0;
        },
        getTotalClasses() {
            if (this.data && this.data.summary && this.data.summary.total_classes !== undefined) {
                return this.data.summary.total_classes;
            }
            return 0;
        },
        getCapacityUtilization() {
            if (this.data && this.data.summary && this.data.summary.capacity_utilization_percentage !== undefined) {
                return this.data.summary.capacity_utilization_percentage;
            }
            return 0;
        },
        getEnrollmentUtilization() {
            if (this.data && this.data.summary && this.data.summary.enrollment_utilization_percentage !== undefined) {
                return this.data.summary.enrollment_utilization_percentage;
            }
            return 0;
        },
        getTopUtilizedRooms() {
            if (this.data && this.data.top_utilized_rooms && Array.isArray(this.data.top_utilized_rooms)) {
                return this.data.top_utilized_rooms.slice(0, 10);
            }
            return [];
        },
        getOthersCount() {
            if (this.data && this.data.top_utilized_rooms && Array.isArray(this.data.top_utilized_rooms)) {
                const top10 = this.data.top_utilized_rooms.slice(0, 10);
                const totalClasses = this.getTotalClasses();
                const top10Classes = top10.reduce((sum, room) => sum + room.class_count, 0);
                return totalClasses - top10Classes;
            }
            return 0;
        },
        getCapacityRanges() {
            if (this.data && this.data.capacity_ranges) {
                // Filter out __typename field from GraphQL response
                const filtered = {};
                Object.keys(this.data.capacity_ranges).forEach(key => {
                    if (key !== '__typename') {
                        filtered[key] = this.data.capacity_ranges[key];
                    }
                });
                return filtered;
            }
            return {};
        },
        async handleBarClick(event, chartContext, config) {
            const roomName = config.w.config.xaxis.categories[config.dataPointIndex];

            if (roomName === 'Others') {
                try {
                    const response = await this.fetchOtherRooms(0, 50);
                    if (response && response.rooms) {
                        this.otherRoomsData = response.rooms;
                        this.showOthersModal = true;
                    } else {
                        this.otherRoomsData = [];
                    }
                } catch (error) {
                    console.error('Error fetching other rooms:', error);
                    this.otherRoomsData = [];
                }
            }
        }
    }
};
</script>

<style scoped>
/* Custom styles for better visual appeal */
.apexcharts-legend {
    padding-top: 20px;
}
</style>
